import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { PlusIcon } from "../../../../Icons";
import {
  getPatientClinicalForms,
  deletePatientClinicalForm,
} from "../../../../services/patientService";
import ClinicalFormList from "./ClinicalFormList";
import ClinicalFormAuditModal from "./ClinicalFormAuditModal";
import FormPickerModal from "./FormPickerModal";
import CollapsibleSection from "../CollapsibleSection";
import useConfirm from "../../../../hooks/useConfirm";
import { sortClinicalFormsByScheduleDateAsc } from "./sortClinicalForms";
import { clinicalFormDraftUrl } from "./clinicalFormDraft";

/**
 * One Eval & Orders section. Action / Upload Document live on the parent tab.
 * Selection is controlled by the parent so marks apply across sections.
 *
 * Default open state: expanded when the section has forms, collapsed when empty
 * (so navigating into the tab does not leave empty “No forms yet” panels open).
 */
const ClinicalFormsSection = ({
  patientId,
  sectionKey,
  title,
  addLabel,
  icon,
  tone,
  canEdit,
  canDelete = false,
  canManageFlags = false,
  patient,
  chartId,
  episodeId,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onFormsLoaded,
  onRemoveMark,
  showSelection = true,
  showMarks = true,
  showCountBadge = false,
  forceOpen = false,
  highlight = false,
  /** When set, Add opens this form type directly (no FormPickerModal). */
  directFormType = null,
  /** Patient detail tab to restore on back (eval | documents). */
  returnTab = "eval",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [auditForm, setAuditForm] = useState(null);
  const [open, setOpen] = useState(false);
  const userOverrodeOpenRef = useRef(false);
  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    userOverrodeOpenRef.current = false;
    setOpen(false);
  }, [patientId, sectionKey, chartId]);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      userOverrodeOpenRef.current = false;
    }
  }, [forceOpen]);

  const handleOpenChange = useCallback((next) => {
    userOverrodeOpenRef.current = true;
    setOpen(next);
  }, []);

  const syncOpenFromForms = useCallback(
    (nextForms) => {
      const hasForms = (nextForms || []).length > 0;
      if (forceOpen) {
        setOpen(true);
        return;
      }
      if (!hasForms) {
        setOpen(false);
        return;
      }
      if (!userOverrodeOpenRef.current) {
        setOpen(true);
      }
    },
    [forceOpen],
  );

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPatientClinicalForms(patientId, {
        section: sectionKey,
        ...(chartId ? { chartId } : {}),
      });
      const next = sortClinicalFormsByScheduleDateAsc(
        res.result?.forms || res.forms || [],
      );
      setForms(next);
      syncOpenFromForms(next);
      onFormsLoaded?.(sectionKey, next);
    } catch (err) {
      toast.error(err.message || "Failed to load forms");
      setForms([]);
      syncOpenFromForms([]);
      onFormsLoaded?.(sectionKey, []);
    } finally {
      setLoading(false);
    }
  }, [patientId, sectionKey, chartId, onFormsLoaded, syncOpenFromForms]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const formNavState = {
    returnTo: `${location.pathname}${location.search || ""}`,
    tab: returnTab,
    evalSection: returnTab === "eval" ? sectionKey : undefined,
  };

  const openNewForm = () => {
    setOpen(true);
    if (directFormType) {
      navigate(
        clinicalFormDraftUrl(patientId, {
          formType: directFormType,
          chartId,
          episodeId,
        }),
        { state: formNavState },
      );
      return;
    }
    setPickerOpen(true);
  };

  const handleOpen = (form) => {
    navigate(`/patients/${patientId}/forms/${form.id}`, {
      state: formNavState,
    });
  };

  const handleDelete = async (form) => {
    const ok = await confirm({
      title: "Delete form?",
      message: form.scheduledVisit
        ? `"${form.formName}" and its scheduled visit will both be removed. This cannot be undone.`
        : `"${form.formName}" and everything saved in it will be permanently removed. This cannot be undone.`,
    });
    if (!ok) return;
    try {
      await deletePatientClinicalForm(patientId, form.id);
      toast.success("Form deleted");
      userOverrodeOpenRef.current = false;
      await loadForms();
    } catch (err) {
      toast.error(err.message || "Failed to delete form");
    }
  };

  const actions =
    canEdit && forms.length > 0 ? (
      <button
        type="button"
        onClick={openNewForm}
        className={`inline-flex items-center gap-1 h-7 px-2.5 text-xs font-semibold border bg-white rounded-md transition-colors ${tone.addTone}`}
      >
        <PlusIcon size={12} />
        {addLabel}
      </button>
    ) : null;

  return (
    <>
      <CollapsibleSection
        title={title}
        icon={icon}
        tone={tone}
        open={open}
        onOpenChange={handleOpenChange}
        className={
          highlight ? "ring-2 ring-indigo-300 ring-offset-1" : undefined
        }
        badge={
          showCountBadge ? (
            <span className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold bg-slate-600 text-white tabular-nums">
              {forms.length}
            </span>
          ) : null
        }
        actions={actions}
      >
        <ClinicalFormList
          forms={forms}
          loading={loading}
          canEdit={canEdit}
          canDelete={canDelete}
          canManageFlags={canManageFlags}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onToggleSelectAll={(selectAll) =>
            onToggleSelectAll?.(
              forms.map((f) => f.id),
              selectAll,
            )
          }
          onOpen={handleOpen}
          onDelete={handleDelete}
          onViewAudit={setAuditForm}
          onRemoveMark={onRemoveMark}
          onAddClick={openNewForm}
          addLabel={addLabel}
          patientId={patientId}
          onAttachmentsChanged={loadForms}
          showSelection={showSelection}
          showMarks={showMarks}
        />
      </CollapsibleSection>

      {!directFormType ? (
        <FormPickerModal
          open={pickerOpen}
          sectionKey={sectionKey}
          sectionTitle={title}
          patientId={patientId}
          patient={patient}
          chartId={chartId}
          episodeId={episodeId}
          returnTo={location.pathname}
          returnTab={formNavState.tab}
          returnEvalSection={formNavState.evalSection}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}

      <ClinicalFormAuditModal
        open={Boolean(auditForm)}
        patientId={patientId}
        form={auditForm}
        onClose={() => setAuditForm(null)}
      />
      {confirmDialog}
    </>
  );
};

export default ClinicalFormsSection;
